import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { readScheduleRows } from '../../realtime/useScheduleRealtime';
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import Form from "react-bootstrap/Form";
import EditJobModal from "./EditJobModal";
import AccountingModal from "./AccountingModal";
// Import centralized date utilities
import { formatDateLocalEnhanced } from "../../utils/dateUtils";
import { hasAnyEditPermission } from './utils/schedulePermissions';
import { selectSellPriceData } from '../../redux/slices/sellPriceSlice';
import { fetchData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { getProjectId } from '../../utils/dataNormalization';
import './PendingInPortal.css';

const TABLE_COLUMNS = [
  { key: 'JobNumber', label: 'Job Number', type: 'text' },
  { key: 'ProjectName', label: 'Project Name', type: 'text' },
  { key: 'ModelNumber', label: 'Model Number', type: 'text' },
  { key: 'DrawingStatus', label: 'Drawing Status', type: 'text' },
  { key: 'ProjectStatus', label: 'Project Status', type: 'text' },
  { key: 'ProjectManager', label: 'Project Manager', type: 'text' },
  { key: 'SalesRep', label: 'Sales Rep', type: 'text' },
  { key: 'OrderDate', label: 'Order Date', type: 'date' },
  { key: 'CSODate', label: 'CSO Date', type: 'date' },
  { key: 'RequestedDeliveryDate', label: 'Requested Delivery Date', type: 'date' },
  { key: 'PaymentTerms', label: 'Payment Terms', type: 'text' },
  { key: 'SellPrice', label: 'Sell Price', type: 'currency' },
];

const GROUP_BY_OPTIONS = [
  { key: '', label: 'No grouping' },
  { key: 'ProjectStatus', label: 'Status', emptyLabel: 'No status' },
  { key: 'ProjectManager', label: 'Project Manager', emptyLabel: 'Unassigned' },
  { key: 'SalesRep', label: 'Sales Rep', emptyLabel: 'Unassigned' },
];

const getSortableDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  const normalizedValue = String(value).trim();
  const dateParts = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateParts) {
    return Date.UTC(
      Number(dateParts[1]),
      Number(dateParts[2]) - 1,
      Number(dateParts[3])
    );
  }

  const timestamp = new Date(normalizedValue).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const parseDollarValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalizedValue = String(value ?? "").replace(/[$,]/g, "");
  const parsedValue = parseFloat(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const PendingInPortal = ({ filteredJobs = null, canEdit = true }) => {
  const dispatch = useDispatch();
  // Always call hooks unconditionally
  const jobScheduleDataFromRedux = useSelector((state) => state.jobSchedule.data);
  const apiUrls = useSelector(selectApiUrls);
  // Get user roles to check permissions
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  
  // Get sell price data from Redux
  const sellPriceData = useSelector(selectSellPriceData);
  
  // Allow editing for users with appropriate permissions
  const hasEditAccess = Boolean(canEdit) && hasAnyEditPermission(userRoles);
  
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAccountingModal, setShowAccountingModal] = useState(false);
  const [accountingJob, setAccountingJob] = useState(null);
  const [visibleRows, setVisibleRows] = useState(50); // Initial number of rows to display
  const [pendingPortalApiJobs, setPendingPortalApiJobs] = useState([]);
  const [pendingPortalSellPrices, setPendingPortalSellPrices] = useState({});
  const [hasLoadedApiJobs, setHasLoadedApiJobs] = useState(false);
  const [loadingApiJobs, setLoadingApiJobs] = useState(false);
  const [apiJobsError, setApiJobsError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending',
  });
  const [groupBy, setGroupBy] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const containerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const loadPendingPortalJobs = async () => {
      if (!apiUrls?.fullSchedule) {
        return;
      }

      setLoadingApiJobs(true);
      setApiJobsError(null);

      try {
        const queryJoiner = apiUrls.fullSchedule.includes('?') ? '&' : '?';
        const url = `${apiUrls.fullSchedule}${queryJoiner}projectStatus=${encodeURIComponent('*Pending*')}`;
        const response = await dispatch(fetchData({ url })).unwrap();
        const raw = response?.data || response || {};
        const sourceItems = Array.isArray(raw) ? raw : Object.values(raw);
        const dedupedJobs = sourceItems
          .map((item) => item?.GeneralProjectInfo || item)
          .filter(Boolean)
          .filter((job, index, arr) =>
          index === arr.findIndex((entry) => getProjectId(entry) === getProjectId(job))
        );
        const sellPrices = sourceItems.reduce((lookup, item) => {
          const job = item?.GeneralProjectInfo || item;
          const projectId = getProjectId(job);
          const priceBreakdownRecord = Array.isArray(item?.PriceBreakdown)
            ? item.PriceBreakdown[0]
            : item?.PriceBreakdown;

          if (projectId && priceBreakdownRecord?.SellPriceAmount !== undefined && priceBreakdownRecord?.SellPriceAmount !== null) {
            lookup[projectId] = priceBreakdownRecord.SellPriceAmount;
          }

          return lookup;
        }, {});

        if (isMounted) {
          setPendingPortalApiJobs(dedupedJobs);
          setPendingPortalSellPrices(sellPrices);
          setHasLoadedApiJobs(true);
        }
      } catch (error) {
        console.error('PendingInPortal: Failed to load jobs from full schedule endpoint:', error);
        if (isMounted) {
          setApiJobsError('Failed to load portal jobs.');
          setHasLoadedApiJobs(true);
        }
      } finally {
        if (isMounted) {
          setLoadingApiJobs(false);
        }
      }
    };

    loadPendingPortalJobs();

    return () => {
      isMounted = false;
    };
  }, [dispatch, apiUrls?.fullSchedule]);

  useGuardedRealtimeList({ apiKey: 'fullSchedule', blocked: loadingApiJobs || showScheduleModal || showAccountingModal,
    params: { projectStatus: '*Pending*' },
    additionalResources: ['portal:general-project-info', 'portal:price-breakdown-info'],
    decode: readScheduleRows,
    apply: ({ rows, core }) => {
      setPendingPortalApiJobs(rows.filter((job, index, all) => index === all.findIndex(other => getProjectId(other) === getProjectId(job))));
      setPendingPortalSellPrices(Object.fromEntries(Object.values(core).flatMap(item => {
        const job = item.GeneralProjectInfo || item;
        const record = Array.isArray(item.PriceBreakdown) ? item.PriceBreakdown[0] : item.PriceBreakdown;
        return getProjectId(job) && record?.SellPriceAmount != null ? [[getProjectId(job), record.SellPriceAmount]] : [];
      })));
      setHasLoadedApiJobs(true);
    },
  });

  const handleRowDoubleClick = (job) => {
    // Check if user has accounting role and show accounting modal
    if (userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling) {
      setAccountingJob(job);
      setShowAccountingModal(true);
      return;
    }
    
    // Only allow double-click functionality if user has edit permissions
    if (hasEditAccess) {
      setSelectedJob(job);
      setShowScheduleModal(true);
    }
    // Do nothing if the user doesn't have permissions
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
    setSelectedJob(null);
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setVisibleRows((prev) => prev + 50); // Load 50 more rows
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);
  const dataToUse = useMemo(() => {
    // When API data is loaded, keep it as the source of truth for CSO jobs.
    if (hasLoadedApiJobs) {
      return Array.isArray(pendingPortalApiJobs) ? pendingPortalApiJobs : [];
    }

    const baseData = Array.isArray(jobScheduleDataFromRedux)
      ? jobScheduleDataFromRedux
      : [];

    // Apply external filtering only to the Redux fallback dataset.
    if (!Array.isArray(filteredJobs)) {
      return baseData;
    }

    const filteredIds = new Set(filteredJobs.map((job) => getProjectId(job)));
    return baseData.filter((job) => filteredIds.has(getProjectId(job)));
  }, [
    filteredJobs,
    hasLoadedApiJobs,
    jobScheduleDataFromRedux,
    pendingPortalApiJobs,
  ]);

  // API fetch handles projectStatus selection; frontend excludes hidden jobs.
  const combinedCSOJobs = useMemo(
    () => (
      Array.isArray(dataToUse)
        ? dataToUse.filter(
            (job) => job.HideFromList !== true && job.HideFromList !== "true"
          )
        : []
    ),
    [dataToUse]
  );

  const getSellPriceValue = useCallback((job) => {
    const projectId = getProjectId(job);
    const sellPriceInfo = sellPriceData[projectId];

    return pendingPortalSellPrices[projectId]
      ?? sellPriceInfo?.sellPrice
      ?? job.SellPriceAmount
      ?? job.SellPrice
      ?? 0;
  }, [pendingPortalSellPrices, sellPriceData]);

  const getSellPriceTotal = useCallback((jobs) => (
    jobs.reduce((sum, job) => sum + parseDollarValue(getSellPriceValue(job)), 0)
  ), [getSellPriceValue]);

  const totalSellPrice = getSellPriceTotal(combinedCSOJobs);

  const sortedCSOJobs = useMemo(() => {
    if (!sortConfig.key) {
      return combinedCSOJobs;
    }

    const column = TABLE_COLUMNS.find(({ key }) => key === sortConfig.key);
    if (!column) {
      return combinedCSOJobs;
    }

    const getValue = (job) => {
      if (column.type === 'currency') {
        return parseDollarValue(getSellPriceValue(job));
      }

      if (column.type === 'date') {
        return getSortableDate(job[column.key]);
      }

      const value = job[column.key];
      return value === null || value === undefined
        ? null
        : String(value).trim();
    };

    return combinedCSOJobs
      .map((job, originalIndex) => ({ job, originalIndex }))
      .sort((left, right) => {
        const leftValue = getValue(left.job);
        const rightValue = getValue(right.job);
        const leftIsBlank = leftValue === null || leftValue === '';
        const rightIsBlank = rightValue === null || rightValue === '';

        // Keep missing values at the bottom in either direction.
        if (leftIsBlank && rightIsBlank) {
          return left.originalIndex - right.originalIndex;
        }
        if (leftIsBlank) return 1;
        if (rightIsBlank) return -1;

        let comparison = 0;
        if (column.type === 'text') {
          comparison = leftValue.localeCompare(rightValue, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        } else {
          comparison = leftValue - rightValue;
        }

        if (comparison === 0) {
          return left.originalIndex - right.originalIndex;
        }

        return sortConfig.direction === 'ascending'
          ? comparison
          : -comparison;
      })
      .map(({ job }) => job);
  }, [
    combinedCSOJobs,
    sortConfig,
    getSellPriceValue,
  ]);

  const handleSort = (key) => {
    setSortConfig((currentSort) => ({
      key,
      direction:
        currentSort.key === key && currentSort.direction === 'ascending'
          ? 'descending'
          : 'ascending',
    }));
    setVisibleRows(50);

    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <i className="bi bi-chevron-expand" aria-hidden="true" />;
    }

    return sortConfig.direction === 'ascending'
      ? <i className="bi bi-chevron-up" aria-hidden="true" />
      : <i className="bi bi-chevron-down" aria-hidden="true" />;
  };

  const jobGroups = useMemo(() => {
    if (sortedCSOJobs.length === 0) {
      return [];
    }

    if (!groupBy) {
      return [{
        key: 'all',
        label: null,
        totalCount: sortedCSOJobs.length,
        jobs: sortedCSOJobs,
      }];
    }

    const groupOption = GROUP_BY_OPTIONS.find(({ key }) => key === groupBy);
    const groupedJobs = new Map();

    sortedCSOJobs.forEach((job) => {
      const rawValue = job[groupBy];
      const normalizedValue = rawValue === null || rawValue === undefined
        ? ''
        : String(rawValue).trim();
      const groupKey = normalizedValue || '__empty__';

      if (!groupedJobs.has(groupKey)) {
        groupedJobs.set(groupKey, {
          key: groupKey,
          label: normalizedValue || groupOption?.emptyLabel || 'Not specified',
          jobs: [],
        });
      }
      groupedJobs.get(groupKey).jobs.push(job);
    });

    const orderedGroups = Array.from(groupedJobs.values()).sort((left, right) => {
      if (left.key === '__empty__') return 1;
      if (right.key === '__empty__') return -1;
      return left.label.localeCompare(right.label, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });

    return orderedGroups.map((group) => ({
        ...group,
        totalCount: group.jobs.length,
        sellPriceTotal: getSellPriceTotal(group.jobs),
      }));
  }, [getSellPriceTotal, groupBy, sortedCSOJobs]);

  const visibleJobGroups = useMemo(() => {
    if (!groupBy) {
      return jobGroups.map((group) => ({
        ...group,
        jobs: group.jobs.slice(0, visibleRows),
      }));
    }

    return jobGroups.map((group) => ({
      ...group,
      jobs: collapsedGroups.has(group.key) ? [] : group.jobs,
    }));
  }, [collapsedGroups, groupBy, jobGroups, visibleRows]);

  const handleGroupChange = (event) => {
    setGroupBy(event.target.value);
    setCollapsedGroups(new Set());
    setVisibleRows(50);

    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  const toggleGroup = (groupKey) => {
    setCollapsedGroups((currentGroups) => {
      const nextGroups = new Set(currentGroups);
      if (nextGroups.has(groupKey)) {
        nextGroups.delete(groupKey);
      } else {
        nextGroups.add(groupKey);
      }
      return nextGroups;
    });
  };

  const expandAllGroups = () => {
    setCollapsedGroups(new Set());
  };

  const collapseAllGroups = () => {
    setCollapsedGroups(new Set(jobGroups.map((group) => group.key)));
  };
  
  // Format date for display - use centralized utility
  const formatDate = (dateString) => formatDateLocalEnhanced(dateString);

  // Format currency
  const formatCurrency = (value) => {
    const numValue = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  return (    
    <div
      className="pending-in-portal-container"
      style={{ overflow: "auto", maxHeight: "80vh" }}
      ref={containerRef}
    >
      {loadingApiJobs && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading portal jobs...</span>
          </Spinner>
          <p>Loading portal jobs...</p>
        </div>
      )}

      {apiJobsError && combinedCSOJobs.length === 0 && (
        <Alert variant="danger">{apiJobsError}</Alert>
      )}

      {/* Combined CSO Jobs Section */}
      <div>
        {combinedCSOJobs.length > 0 && (
          <div
            style={{
              fontWeight: "700",
              fontSize: "1.1rem",
              marginBottom: "16px",
            }}
          >
            Total Sell Price for All Items: {formatCurrency(totalSellPrice)}
          </div>
        )}

        <h4>CSO Jobs ({combinedCSOJobs.length})</h4>

        <div className="pending-in-portal__table-controls">
          <div style={{ fontWeight: "600" }}>
            Total Sell Price: {formatCurrency(totalSellPrice)}
          </div>
          <div className="pending-in-portal__grouping-tools">
            {groupBy && (
              <div className="pending-in-portal__group-actions" aria-label="Group display controls">
                <button
                  type="button"
                  onClick={expandAllGroups}
                  disabled={collapsedGroups.size === 0}
                >
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={collapseAllGroups}
                  disabled={
                    jobGroups.length === 0 ||
                    jobGroups.every((group) => collapsedGroups.has(group.key))
                  }
                >
                  Collapse all
                </button>
              </div>
            )}
            <Form.Group className="pending-in-portal__group-control" controlId="pending-portal-group-by">
              <Form.Label>Group by</Form.Label>
              <Form.Select value={groupBy} onChange={handleGroupChange}>
                {GROUP_BY_OPTIONS.map((option) => (
                  <option key={option.key || 'none'} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </div>
        
        <Table striped bordered hover responsive className="schedule-responsive-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((column) => (
                <th
                  key={column.key}
                  aria-sort={
                    sortConfig.key === column.key
                      ? sortConfig.direction
                      : undefined
                  }
                  className={column.type === 'currency' ? 'text-end' : undefined}
                >
                  <button
                    type="button"
                    className="pending-in-portal__sort-button"
                    onClick={() => handleSort(column.key)}
                    title={`Sort by ${column.label}`}
                  >
                    <span>{column.label}</span>
                    {renderSortIcon(column.key)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleJobGroups.length > 0 ? (
              visibleJobGroups.map((group) => (
                <React.Fragment key={group.key}>
                  {group.label && (
                    <tr className="pending-in-portal__group-row">
                      <th colSpan={TABLE_COLUMNS.length} scope="rowgroup">
                        <button
                          type="button"
                          className="pending-in-portal__group-toggle"
                          onClick={() => toggleGroup(group.key)}
                          aria-expanded={!collapsedGroups.has(group.key)}
                        >
                          <span className="pending-in-portal__group-name">
                            <i
                              className={`bi ${
                                collapsedGroups.has(group.key)
                                  ? 'bi-chevron-right'
                                  : 'bi-chevron-down'
                              }`}
                              aria-hidden="true"
                            />
                            <span>{group.label}</span>
                          </span>
                          <span className="pending-in-portal__group-summary">
                            <span className="pending-in-portal__group-count">
                              {group.totalCount} {group.totalCount === 1 ? 'job' : 'jobs'}
                            </span>
                            <span className="pending-in-portal__group-total">
                              Sell Price: {formatCurrency(group.sellPriceTotal)}
                            </span>
                          </span>
                        </button>
                      </th>
                    </tr>
                  )}
                  {group.jobs.map((job) => (
                    <tr
                      key={getProjectId(job)}
                      onDoubleClick={() => handleRowDoubleClick(job)}
                      style={{ cursor: hasEditAccess ? 'pointer' : 'default' }}
                      title={hasEditAccess ? "Double-click to edit" : ""}
                    >
                      <td data-label="Job Number">{job.JobNumber}</td>
                      <td data-label="Project Name">{job.ProjectName}</td>
                      <td data-label="Model Number">{job.ModelNumber}</td>
                      <td data-label="Drawing Status">{job.DrawingStatus}</td>
                      <td data-label="Project Status">{job.ProjectStatus || ''}</td>
                      <td data-label="Project Manager">{job.ProjectManager}</td>
                      <td data-label="Sales Rep">{job.SalesRep}</td>
                      <td data-label="Order Date">{formatDate(job.OrderDate)}</td>
                      <td data-label="CSO Date">{formatDate(job.CSODate)}</td>
                      <td data-label="Requested Delivery">{formatDate(job.RequestedDeliveryDate)}</td>
                      <td data-label="Payment Terms">{job.PaymentTerms}</td>
                      <td data-label="Sell Price" style={{ textAlign: 'right' }}>
                        {formatCurrency(getSellPriceValue(job))}
                      </td>
                      {(hasEditAccess || (userRolesMapped && userRolesMapped.Accounting)) && (
                        <td data-label="Actions" className="schedule-card-action-cell">
                          <button type="button" className="btn btn-primary schedule-card-action" onClick={() => handleRowDoubleClick(job)}>
                            Open job
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))
            ) : (
              <tr>
              <td colSpan={TABLE_COLUMNS.length} className="schedule-empty-row">No CSO jobs</td>
              </tr>
)}
          </tbody>
        </Table>
      </div>

      {!groupBy && visibleRows < sortedCSOJobs.length && (
        <div style={{ textAlign: "center", padding: "10px" }}>
          Loading more rows...
        </div>
      )}

      {/* EditJobModal for editing job details */}
      <Modal 
        className="job-schedule-modal"
        show={showScheduleModal} 
        onHide={() => {
          // Get reference to the modal component
          const modalComponent = document.querySelector(".modal-content")
            ?.closest(".modal");
          
          // Dispatch a click event on the EditJobModal's internal close handler
          // which will trigger the confirmation dialog if there are unsaved changes
          const closeButton = modalComponent?.querySelector(".modal-header .btn-close");
          if (closeButton) {
            closeButton.click();
          } else {
            // Fallback if button not found
            handleCloseScheduleModal();
          }
        }} 
        size="xl"
      >
        <EditJobModal
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          handleCloseModal={handleCloseScheduleModal}
          handleSaveChanges={() => {}}
          canEdit={hasEditAccess}
        />
      </Modal>
      
      {/* Accounting Modal for accounting users */}
      <AccountingModal
        show={showAccountingModal}
        onHide={() => setShowAccountingModal(false)}
        job={accountingJob}
      />
    </div>
  );
};

export default PendingInPortal;
